import React, { useState, useCallback, useEffect } from 'react';
import { Modal } from '../../';
import { Typography } from 'antd';
import TagsSelector from '../../TagsSelector';
import { useNotificationContext } from '../../../providers/NotificationProvider';
import { useAddTagsToCloudAccountMutation, useCreateOrEnsureTagsMutation } from '../../../api';

import './MigrationAppliancesAddTagsModal.scss';

const { Text } = Typography;

const MigrationAppliancesAddTagsModal = ({
    applianceName,
    applianceId,
    open,
    onClose,
    onSuccess,
    initialTags = [],
}) => {
    const [tags, setTags] = useState([]);
    const [newTags, setNewTags] = useState([]);
    const { notifySuccess, notifyError } = useNotificationContext();
    const [addTagsToCloudAccount, { isLoading }] = useAddTagsToCloudAccountMutation();
    const [createOrEnsureTags] = useCreateOrEnsureTagsMutation();

    const handleTagsChange = useCallback((allTags, newTagsList = []) => {
        setTags(allTags);
        setNewTags(newTagsList);
    }, []);

    // If initial tags are passed in from the appliance show them in the modal
    useEffect(() => {
        if (open && initialTags?.length > 0) {
            setTags(initialTags);
        }
    }, [initialTags, open]);

    const handleConfirm = useCallback(async () => {
        if (!tags.length && !initialTags.length) {
            notifyError({ description: 'Please add at least one tag.' });
            return;
        }

        try {
            if (newTags.length) {
                await createOrEnsureTags({
                    orgId: sessionStorage.getItem('userOrg'),
                    tags: newTags,
                }).unwrap();
            }

            await addTagsToCloudAccount({ applianceId, tags }).unwrap();

            notifySuccess({
                description: `Successfully updated tags for ${applianceName}`,
            });
            setTags([]);
            setNewTags([]);
            onSuccess?.();
            onClose();
        } catch (error) {
            console.error('Error adding tags:', error);
            notifyError({ description: 'Failed to add tags. Please try again.' });
        }
    }, [
        tags,
        newTags,
        applianceId,
        applianceName,
        initialTags.length,
        createOrEnsureTags,
        addTagsToCloudAccount,
        notifySuccess,
        notifyError,
        onSuccess,
        onClose,
    ]);

    const handleCancel = useCallback(() => {
        setTags([]);
        setNewTags([]);
        onClose();
    }, [onClose]);

    return (
        <Modal
            title={`Add or Remove Tags: ${applianceName}`}
            open={open}
            onOk={handleConfirm}
            onCancel={handleCancel}
            okText="Confirm"
            cancelText="Cancel"
            confirmLoading={isLoading}
            className="add-tags-modal"
            width={500}
        >
            <div className="tags-modal-content">
                <TagsSelector
                    value={tags}
                    onChange={handleTagsChange}
                    placeholder="Enter tag name"
                    disabled={isLoading}
                />
            </div>
        </Modal>
    );
};

export default MigrationAppliancesAddTagsModal;
